/**
 * Pure presentation component for {@link TabFocusGuardContainer}.
 *
 * Renders a `flex-1` Box that conditionally applies
 * `web:pointer-events-none` when the host tab is blurred. See the
 * container's module remark for the full rationale and a link to
 * `.claude/rules/product-fix-for-test-pass.md`.
 * @module components/TabFocusGuard/TabFocusGuardView
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";

/** Props for {@link TabFocusGuardView}. */
interface Props {
  /** Whether the host tab is currently focused. */
  readonly isFocused: boolean;
  /** Tab screen content to render. */
  readonly children: React.ReactNode;
}

/**
 * Renders the children inside a `flex-1` Box that becomes
 * `web:pointer-events-none` while the host tab is blurred. On native
 * the className is a no-op — `react-native-screens` already detaches
 * inactive tabs.
 * @param props - View props
 * @param props.isFocused - Whether the host tab is currently focused
 * @param props.children - Tab screen content to render
 */
const TabFocusGuardView = ({ isFocused, children }: Props) => (
  <Box className={`flex-1 ${isFocused ? "" : "web:pointer-events-none"}`}>
    {children}
  </Box>
);

TabFocusGuardView.displayName = "TabFocusGuardView";

export default memo(TabFocusGuardView);
